import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Award, Upload, Trash2, FileText, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import { StatusBadge } from "@/components/Badges";
import { api, formatApiError } from "@/lib/api";

export default function AchievementsPage() {
  const [meta, setMeta] = useState({ tingkat: [], kategori: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    judul: "", tingkat: "", kategori: "", tahun: new Date().getFullYear(),
    penyelenggara: "", sertifikat_path: "", sertifikat_filename: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/achievements/mine");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
    load();
  }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("sertifikat_path", data.path);
      upd("sertifikat_filename", data.filename);
      toast.success("Sertifikat terunggah");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal unggah");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/achievements", { ...form, tahun: Number(form.tahun) });
      toast.success("Prestasi berhasil ditambahkan, menunggu verifikasi");
      setOpen(false);
      setForm({ judul: "", tingkat: "", kategori: "", tahun: new Date().getFullYear(), penyelenggara: "", sertifikat_path: "", sertifikat_filename: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/achievements/${id}`);
      toast.success("Prestasi dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls = "w-full rounded-lg border border-[#E5DFD7] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#E5A93C] focus:ring-2 focus:ring-[#E5A93C]/30 transition";
  const labelCls = "block text-xs uppercase tracking-widest font-bold text-[#8C827A] mb-1.5";

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-[#1F1917]">Prestasi Saya</h1>
            <p className="text-[#8C827A] mt-1">Catat dan unggah sertifikat prestasi Anda.</p>
          </div>
          <button data-testid="achievement-add-btn" onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-[#4A2E2B] text-white px-5 py-3 rounded-lg font-bold hover:bg-[#3a2320] transition">
            <Plus className="h-4 w-4" /> Tambah Prestasi
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#B85C38]" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-[#E5DFD7] p-16 text-center">
            <Award className="h-12 w-12 text-[#C29B72] mx-auto mb-3" />
            <p className="font-display font-semibold text-[#1F1917]">Belum ada prestasi</p>
            <p className="text-sm text-[#8C827A] mt-1">Klik "Tambah Prestasi" untuk mencatat capaian pertama Anda.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {items.map((it) => (
              <div key={it.id} data-testid="achievement-card"
                className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition">
                <div className="h-11 w-11 rounded-xl bg-[#E5A93C]/15 flex items-center justify-center shrink-0">
                  <Award className="h-5 w-5 text-[#B85C38]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold text-[#1F1917]">{it.judul}</h3>
                    <StatusBadge value={it.status} />
                  </div>
                  <div className="text-sm text-[#4A423F] mt-1">
                    {it.tingkat} · {it.kategori} · {it.tahun}{it.penyelenggara ? ` · ${it.penyelenggara}` : ""}
                  </div>
                  {it.feedback && <div className="mt-2 text-xs bg-rose-50 text-rose-800 rounded-lg px-3 py-2">Catatan: {it.feedback}</div>}
                  {it.sertifikat_path && (
                    <a href={`${api.defaults.baseURL}/files/${it.sertifikat_path}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#B85C38] hover:underline">
                      <FileText className="h-3.5 w-3.5" /> {it.sertifikat_filename || "Lihat Sertifikat"}
                    </a>
                  )}
                </div>
                <button data-testid="achievement-delete-btn" onClick={() => remove(it.id)}
                  className="p-2 rounded-lg text-[#D9383A] hover:bg-rose-50 transition"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-[#E5DFD7]">
              <h2 className="font-display text-xl font-extrabold text-[#1F1917]">Tambah Prestasi</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-[#F7F4EF]"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className={labelCls}>Judul Prestasi / TKK / TKU</label>
                <input data-testid="achievement-input-judul" className={inputCls} value={form.judul}
                  onChange={(e) => upd("judul", e.target.value)} required placeholder="cth: Juara 1 LT III" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Tingkat</label>
                  <select data-testid="achievement-select-tingkat" className={inputCls} value={form.tingkat}
                    onChange={(e) => upd("tingkat", e.target.value)} required>
                    <option value="">Pilih</option>
                    {meta.tingkat.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Kategori</label>
                  <select data-testid="achievement-select-kategori" className={inputCls} value={form.kategori}
                    onChange={(e) => upd("kategori", e.target.value)} required>
                    <option value="">Pilih</option>
                    {meta.kategori.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Tahun Raih</label>
                  <input data-testid="achievement-input-tahun" type="number" className={inputCls} value={form.tahun}
                    onChange={(e) => upd("tahun", e.target.value)} required min="1990" max="2030" />
                </div>
                <div>
                  <label className={labelCls}>Penyelenggara</label>
                  <input className={inputCls} value={form.penyelenggara}
                    onChange={(e) => upd("penyelenggara", e.target.value)} placeholder="cth: Kwarcab Depok" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Sertifikat (JPG/PNG/PDF, maks 5MB)</label>
                <label data-testid="achievement-upload-sertifikat"
                  className="flex items-center justify-center gap-2 border-2 border-dashed border-[#E5DFD7] rounded-lg py-6 cursor-pointer hover:border-[#E5A93C] hover:bg-[#F7F4EF] transition text-sm text-[#8C827A]">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {form.sertifikat_filename ? <span className="text-[#4A2E2B] font-semibold">{form.sertifikat_filename}</span> : "Klik untuk unggah sertifikat"}
                  <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf"
                    onChange={(e) => handleUpload(e.target.files[0])} />
                </label>
              </div>
              <button data-testid="achievement-submit-btn" type="submit" disabled={saving || uploading}
                className="w-full bg-[#4A2E2B] text-white py-3 rounded-lg font-bold hover:bg-[#3a2320] transition disabled:opacity-60 flex items-center justify-center gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan Prestasi
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
